import { useEffect, useMemo, useState } from 'react'
import { useSearchParams } from 'react-router'
import { useTranslation } from 'react-i18next'
import {
  Bot,
  PhoneCall,
  PhoneForwarded,
  PhoneIncoming,
  PhoneOutgoing,
  Star,
  Voicemail as VoicemailIcon,
  X,
} from 'lucide-react'
import { useEstDesktop } from '@/hooks/useEstDesktop'
import { cn } from '@/lib/utils'
import { useUIStore } from '@/stores/useUIStore'
import { useDialer } from '@/hooks/useDialer'
import { useCallLogs } from '@/api/calls'
import { useFirstCompany } from '@/api/profile'
import { useMarkAllVoicemailsRead } from '@/api/voicemails'
import type { CallFilter } from '@/types/calls'
import { CallHistoryList } from './CallHistoryList'
import { ContactDetail } from './ContactDetail'
import { DialerPane } from './DialerPane'
import { BoutonAppeler } from './BoutonAppeler'

const TONE_CLASSES: Record<string, { active: string; activeDark: string }> = {
  indigo: { active: 'bg-indigo-50 text-indigo-700', activeDark: 'bg-indigo-500/20 text-indigo-300' },
  green: { active: 'bg-green-50 text-green-700', activeDark: 'bg-green-500/15 text-green-400' },
  red: { active: 'bg-red-50 text-red-700', activeDark: 'bg-red-500/15 text-red-400' },
  orange: { active: 'bg-orange-50 text-orange-700', activeDark: 'bg-orange-500/15 text-orange-400' },
  violet: { active: 'bg-violet-50 text-violet-700', activeDark: 'bg-violet-500/15 text-violet-300' },
  amber: { active: 'bg-amber-50 text-amber-700', activeDark: 'bg-amber-500/15 text-amber-400' },
}

export function PhoneTab() {
  const { t } = useTranslation()
  const [searchParams, setSearchParams] = useSearchParams()

  const FILTERS: {
    id: CallFilter
    label: string
    icon?: typeof PhoneCall
    tone?: string
  }[] = [
    { id: 'keypad', label: t('phone.keypad', 'Clavier'), icon: PhoneCall, tone: 'indigo' },
    { id: 'all', label: t('phone.all', 'Tous') },
    {
      id: 'outbound',
      label: t('phone.outbound', 'Sortants'),
      icon: PhoneOutgoing,
      tone: 'green',
    },
    {
      id: 'inbound',
      label: t('phone.inbound', 'Entrants'),
      icon: PhoneIncoming,
      tone: 'indigo',
    },
    { id: 'missed', label: t('phone.missed', 'Manqués'), tone: 'red' },
    {
      id: 'voicemail',
      label: t('voicemail.voicemails', 'Vocaux'),
      icon: VoicemailIcon,
      tone: 'orange',
    },
    {
      id: 'ai',
      label: t('phone.aiTab', 'IA'),
      icon: Bot,
      tone: 'violet',
    },
    {
      id: 'favorites',
      label: t('favorites.tab', 'Favoris'),
      icon: Star,
      tone: 'amber',
    },
  ]
  const darkMode = useUIStore((s) => s.darkMode)
  const selectedPhoneForSms = useUIStore((s) => s.selectedPhoneForSms)
  const setSelectedPhoneForSms = useUIStore((s) => s.setSelectedPhoneForSms)
  const setSelectedCallForDetail = useUIStore((s) => s.setSelectedCallForDetail)

  const filter = (searchParams.get('filter') as CallFilter) || 'keypad'
  const setFilter = (next: CallFilter) => {
    const params = new URLSearchParams(searchParams)
    params.set('filter', next)
    setSearchParams(params, { replace: true })
  }

  // Auto mark-as-read : dès que l'agent consulte la liste (n'importe quel
  // filtre sauf 'keypad' où il vient juste pour passer un appel), on marque
  // tous les voicemails non lus comme lus. Le transcript est visible dans la
  // liste → on considère que l'user a "lu" le message.
  const { data: company } = useFirstCompany()
  const callLogsQuery = useCallLogs('all')
  const unreadVoicemailIds = useMemo(() => {
    const firstPage = callLogsQuery.data?.pages[0]
    if (!firstPage?.calls) return []
    return firstPage.calls
      .filter((c) => c.type === 'voicemail' && c.is_read === false)
      .map((c) => c.id)
  }, [callLogsQuery.data])
  const markAllRead = useMarkAllVoicemailsRead(company?.id ?? null)
  useEffect(() => {
    // 'favorites' n'est pas une vue d'appels → ne pas marquer les vocaux lus.
    if (filter === 'keypad' || filter === 'favorites') return
    if (!company?.id) return
    if (unreadVoicemailIds.length === 0) return
    if (markAllRead.isPending) return
    markAllRead.mutate(unreadVoicemailIds)
  }, [filter, company?.id, unreadVoicemailIds, markAllRead])

  // Numéro présenté + départ d'appel (partagé avec le clavier de la Boîte de réception).
  // Le click-to-call (?call=…&auto=1) est capté par InboxTab, qui ouvre le clavier.
  const { placeCall } = useDialer()

  // Click ligne historique → ouvre le fil de la personne (PAS appel)
  const handleCallClick = (number: string) => {
    setSelectedPhoneForSms(number)
    setSelectedCallForDetail(null)
  }

  // Bouton Phone vert (voicemail) ou DialPad → appel direct
  const handleDirectCall = (number: string) => {
    placeCall(number)
  }

  // Colonne droite : le fil de la personne. Le panneau « Détails de l'appel »
  // a disparu : le fil montre tout (résumé, audio, qui a répondu) et chaque
  // appel y porte « copier » et les tâches suggérées. Sans appel choisi :
  // sur mobile le clavier (filtre « Clavier »), sur bureau un état vide ; le
  // clavier s'y ouvre par l'icône « Composer » de la liste.
  const estDesktop = useEstDesktop()
  const [composer, setComposer] = useState(false)
  useEffect(() => {
    if (selectedPhoneForSms) setComposer(false)
  }, [selectedPhoneForSms])
  const rightMode: 'keypad' | 'sms' | 'vide' = selectedPhoneForSms
    ? 'sms'
    : !estDesktop || composer
      ? 'keypad'
      : 'vide'
  // Sur bureau, « Clavier » n'est pas un filtre : la liste montre « Tous ».
  const filtreActif: CallFilter = estDesktop && filter === 'keypad' ? 'all' : filter

  return (
    <div className="w-full h-full flex flex-col md:flex-row overflow-hidden">
      {/* Liste appels (gauche desktop, masqué si keypad sur mobile) */}
      <div
        className={cn(
          'flex-1 md:w-[520px] xl:w-[600px] 2xl:w-[720px] md:flex-none md:border-r flex flex-col h-full overflow-hidden',
          darkMode ? 'md:border-slate-800' : 'md:border-slate-200',
          filter === 'keypad' ? 'hidden md:flex' : 'flex',
        )}
      >
        <div
          className={cn(
            'flex-none px-3 pt-3 pb-2 transition-colors',
            darkMode ? 'bg-slate-900' : 'bg-white',
          )}
        >
          <div className="flex items-center gap-2">
          <div className="flex flex-1 min-w-0 gap-1 overflow-x-auto no-scrollbar">
            {FILTERS.map(({ id, label, icon: Icon, tone }) => {
              const isActive = filtreActif === id
              const tones = tone ? TONE_CLASSES[tone] : undefined
              const activeClass = tones
                ? darkMode
                  ? tones.activeDark
                  : tones.active
                : darkMode
                  ? 'bg-slate-700 text-white'
                  : 'bg-slate-100 text-slate-900'
              return (
                <button
                  key={id}
                  type="button"
                  onClick={() => setFilter(id)}
                  className={cn(
                    'px-2.5 py-1 rounded text-[11px] font-medium whitespace-nowrap transition-all flex items-center gap-1',
                    // Bureau : pastilles compactes, bordées.
                    'md:rounded-full md:px-2.5 md:py-0.5 md:border',
                    id === 'keypad' && 'md:hidden',
                    isActive
                      ? cn(activeClass, 'md:border-transparent')
                      : darkMode
                        ? 'text-slate-500 hover:text-slate-300 md:border-slate-700'
                        : 'text-slate-400 hover:text-slate-600 md:border-slate-200',
                  )}
                >
                  {Icon && <Icon className="w-3 h-3" />}
                  {label}
                </button>
              )
            })}
          </div>
          {/* Bureau seulement : sur mobile, le filtre « Clavier » ouvre le composeur. */}
          <div className="hidden md:flex">
            <BoutonAppeler
              onClick={() => {
                setSelectedPhoneForSms(null)
                setComposer(true)
              }}
              actif={composer && !selectedPhoneForSms}
              darkMode={darkMode}
            />
          </div>
          </div>
        </div>

        <div
          className={cn(
            'flex-1 overflow-y-auto min-h-0',
            darkMode ? 'bg-slate-900' : 'bg-white',
          )}
        >
          <CallHistoryList
            filter={filter}
            darkMode={darkMode}
            onCallClick={handleCallClick}
            onDirectCall={handleDirectCall}
          />
        </div>
      </div>

      {/* Colonne droite — 3 modes : keypad / sms / detail */}
      <div
        className={cn(
          'flex-1 flex flex-col overflow-hidden',
          darkMode ? 'bg-slate-900' : 'bg-slate-50/50',
          filter !== 'keypad' && rightMode === 'keypad' ? 'hidden md:flex' : 'flex',
        )}
      >
        {rightMode === 'sms' && selectedPhoneForSms && (
          <ContactDetail
            key={selectedPhoneForSms}
            phoneNumber={selectedPhoneForSms}
            darkMode={darkMode}
            onClose={() => setSelectedPhoneForSms(null)}
            showProfileToggle={false}
          />
        )}

        {rightMode === 'keypad' && (
          <div className="relative flex-1 flex flex-col overflow-hidden">
            {estDesktop && (
              <button
                type="button"
                onClick={() => setComposer(false)}
                title={t('common.close', 'Fermer')}
                className={cn(
                  'absolute top-3 right-3 z-10 p-2 rounded-lg transition-colors',
                  darkMode
                    ? 'hover:bg-slate-800 text-slate-400 hover:text-white'
                    : 'hover:bg-gray-100 text-gray-500 hover:text-gray-700',
                )}
              >
                <X className="h-4 w-4" />
              </button>
            )}
            <DialerPane darkMode={darkMode} onFerme={() => setComposer(false)} />
          </div>
        )}

        {rightMode === 'vide' && (
          <div className="flex-1 flex items-center justify-center">
            <div className="text-center">
              <PhoneForwarded
                className={cn('h-10 w-10 mx-auto mb-3 opacity-30', darkMode ? 'text-slate-600' : 'text-slate-300')}
              />
              <p className={cn('text-sm', darkMode ? 'text-slate-500' : 'text-slate-400')}>
                {t('history.selectCall', 'Sélectionnez un appel')}
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
